import { CameraTargetProps, Number3 } from '@abyss/3d-viewer';
import queryString from 'query-string';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { useEffect, useRef } from 'react';
import * as globalState from '@/state';
import * as analysisState from '@/components/Analysis/state';
import {
  blisterToAdd,
  selectedPointOfInterestId,
} from '@/components/Analysis/modules/pointOfInterest/state';
import { mapLocationToCurrentImage } from './Viewer/hooks/useImageFromLocationId';
import { useGetAssemblyByTagNameLazyQuery } from '@/__generated__/graphql';
import { useGetStructureId } from '@/hooks/useGetStructureId';
import { shouldDisableCleanDefect } from '@/utils/defects';
import { AnalysisDrawerLeft, LastDeeplinkParameters } from '@/types';
import { toggleValue, updateUrl } from '@/utils';

export const updateCameraTargetUrlParameters = (cameraTarget: CameraTargetProps) => {
  const [lx, ly, lz] = cameraTarget.lookAt;
  updateUrl('look.x', lx.toString());
  updateUrl('look.y', ly.toString());
  updateUrl('look.z', lz.toString());
  const [px, py, pz] = cameraTarget.position ?? [0, 0, 0];
  updateUrl('position.x', px.toString());
  updateUrl('position.y', py.toString());
  updateUrl('position.z', pz.toString());
  updateUrl('fov', cameraTarget.fov ? cameraTarget.fov.toString() : '0');
};

const parseFloatParameter = (queryParam: string | (string | null)[]): number => {
  if (typeof queryParam === 'string') {
    return Number.parseFloat(queryParam);
  } else {
    return 0;
  }
};

export const useQueryParametersFromState = () => {
  const statesLoaded = useAtomValue(globalState.statesLoaded);
  const selectedSpherical = useAtomValue(analysisState.viewerNextCurrentSpherical);
  const selectedStructureDefectsLegends = useAtomValue(
    analysisState.selectedStructureDefectsLegends
  );
  const selectedAssemblyName = useAtomValue(analysisState.selectedAssemblyName);
  const pointOfInterestId = useAtomValue(selectedPointOfInterestId);
  const selectedTemplateFilters = useAtomValue(analysisState.selectedTemplateFiltersState);
  const cameraTarget: CameraTargetProps | undefined = useAtomValue(analysisState.cameraTarget);
  const showOnlySelectedPoi = useAtomValue(analysisState.showOnlySelectedPoi);

  useEffect(() => {
    if (statesLoaded) {
      updateUrl(
        'defects',
        selectedStructureDefectsLegends
          ?.filter((defect) => defect.isEnabled === true)
          .map((defect) => JSON.stringify(defect.id))
      );
    }
  }, [selectedStructureDefectsLegends, statesLoaded]);

  useEffect(() => {
    if (statesLoaded) {
      updateUrl('assembly', selectedAssemblyName);
    }
  }, [selectedAssemblyName, statesLoaded]);

  useEffect(() => {
    if (statesLoaded) {
      updateUrl('selectedSpherical', selectedSpherical?.name);
    }
  }, [statesLoaded, selectedSpherical]);

  useEffect(() => {
    if (statesLoaded) {
      updateUrl('poi', pointOfInterestId);
    }
  }, [pointOfInterestId, statesLoaded]);

  useEffect(() => {
    if (statesLoaded) {
      updateUrl('showOnlySelectedPoi', showOnlySelectedPoi ? 'true' : 'false');
    }
  }, [showOnlySelectedPoi, statesLoaded]);

  useEffect(() => {
    if (statesLoaded) {
      // [{id:'class',o:['Flange','Support']}]
      const filters = selectedTemplateFilters.map((filter) => ({
        id: filter.id,
        o: [...filter.value],
      }));
      updateUrl('filters', JSON.stringify(filters));
    }
  }, [selectedTemplateFilters, statesLoaded]);

  useEffect(() => {
    if (statesLoaded && cameraTarget) {
      updateCameraTargetUrlParameters(cameraTarget);
    }
  }, [cameraTarget, statesLoaded]);
};

export const useSetStateFromQueryParameters = () => {
  const [lastDeeplinkUrlParameters, setLastDeeplinkUrlParameters] = useAtom(
    analysisState.lastDeeplinkUrlParameters
  );
  const inspectionMetadata = useAtomValue(analysisState.inspectionMetadata);
  const structureLocations = useAtomValue(analysisState.structureLocations);
  const setSelectedSpherical = useSetAtom(analysisState.selectedSpherical);
  const [statesLoaded, setStatesLoaded] = useAtom(globalState.statesLoaded);
  const setSelectedStructureDefectsLegends = useSetAtom(
    analysisState.selectedStructureDefectsLegends
  );
  const setSelectedTemplateFilters = useSetAtom(analysisState.selectedTemplateFiltersState);
  const setSelectedAssemblyName = useSetAtom(analysisState.selectedAssemblyName);
  const setSelectedAssemblyId = useSetAtom(analysisState.selectedAssemblyId);
  const setPointOfInterestId = useSetAtom(selectedPointOfInterestId);
  const setBlisterToAdd = useSetAtom(blisterToAdd);
  const setAbyssViewerState = useSetAtom(globalState.abyssViewerState);
  const setShowOnlySelectedPoi = useSetAtom(analysisState.showOnlySelectedPoi);
  const [drawerLeft, setDrawerLeft] = useAtom(analysisState.drawerLeft);

  const setPointBudget = useSetAtom(globalState.pointBudget);
  const setEnableAdaptivePointBudget = useSetAtom(globalState.enableAdaptivePointBudget);

  const setCameraTarget = useSetAtom(analysisState.cameraTarget);
  const [getAssemblyId] = useGetAssemblyByTagNameLazyQuery();
  const structureId = useGetStructureId();

  const isComplete = useRef(false);

  useEffect(() => {
    if (!statesLoaded && lastDeeplinkUrlParameters) {
      setStatesLoaded(true);
    }
  }, [statesLoaded, setStatesLoaded, lastDeeplinkUrlParameters]);

  useEffect(() => {
    if (isComplete.current) return;
    const query = window.location.search;
    if (query === lastDeeplinkUrlParameters?.query) return;

    setStatesLoaded(false);

    if (inspectionMetadata && structureLocations !== undefined && structureId) {
      const parameters: LastDeeplinkParameters = { query, time: performance.now() };
      const parsed = queryString.parse(query, { arrayFormat: 'comma' });
      if (parsed.defects) {
        setSelectedStructureDefectsLegends((currentDefects) => {
          return currentDefects?.map((defect) => {
            if (parsed.defects?.includes(JSON.stringify(defect.id))) {
              return { ...defect, isEnabled: shouldDisableCleanDefect(defect.name) };
            }
            return defect;
          });
        });
      }
      if (parsed.assembly) {
        const tagName = parsed.assembly.toString();
        if (tagName) {
          getAssemblyId({ variables: { tagName, structureId } }).then(({ data }) => {
            const assemblyId = data?.assemblyByTagName?.id;
            if (assemblyId) setSelectedAssemblyId(assemblyId);
          });
        }
        setSelectedAssemblyName(tagName);
      }

      if (parsed.poi) {
        setPointOfInterestId(parsed.poi.toString());
        setBlisterToAdd({ state: 'Reviewing' });
      }

      if (parsed.poi && parsed.showOnlySelectedPoi) {
        setShowOnlySelectedPoi(parsed.showOnlySelectedPoi === 'true');
        setDrawerLeft(toggleValue<AnalysisDrawerLeft>(drawerLeft, 'visibility'));
      }

      if (
        !parsed.selectedSpherical &&
        parsed['look.x'] &&
        parsed['look.y'] &&
        parsed['look.z'] &&
        parsed['position.x'] &&
        parsed['position.y'] &&
        parsed['position.z'] &&
        parsed.fov
      ) {
        const fov = Number.parseFloat(parsed.fov.toString());
        const lookAt: Number3 = [
          parseFloatParameter(parsed['look.x']),
          parseFloatParameter(parsed['look.y']),
          parseFloatParameter(parsed['look.z']),
        ];
        const position: Number3 = [
          parseFloatParameter(parsed['position.x']),
          parseFloatParameter(parsed['position.y']),
          parseFloatParameter(parsed['position.z']),
        ];
        const cameraTarget: CameraTargetProps = {
          position,
          lookAt,
          fov: fov > 0 ? fov : undefined,
        };
        setCameraTarget(cameraTarget);
        parameters['lookAt'] = lookAt;
      }

      if (parsed.selectedSpherical) {
        const selectedViewpoint = structureLocations.find(
          (location) => location.name === parsed.selectedSpherical
        );

        const fov = parsed.fov ? parseFloatParameter(parsed.fov) : undefined;
        const lookAt =
          parsed['look.x'] && parsed['look.y'] && parsed['look.z']
            ? {
                x: parseFloatParameter(parsed['look.x']),
                y: parseFloatParameter(parsed['look.y']),
                z: parseFloatParameter(parsed['look.z']),
              }
            : undefined;
        const selectedSpherical = mapLocationToCurrentImage(selectedViewpoint, lookAt, fov);
        setAbyssViewerState({ mode: 'Spherical' });
        setSelectedSpherical(selectedSpherical);
        parameters['selectedSpherical'] = selectedSpherical;
        if (lookAt) {
          parameters['lookAt'] = [lookAt.x, lookAt.y, lookAt.z];
        }
      }

      if (parsed.filters) {
        const filters = JSON.parse(parsed.filters.toString());
        const mappedFilteres = filters.map(({ id, o }: { id: string; o: string[] }) => ({
          id,
          value: [...o],
        }));
        setSelectedTemplateFilters(mappedFilteres);
      }

      if (parsed.pointBudget && !Array.isArray(parsed.pointBudget)) {
        if (parsed.pointBudget === 'auto') {
          setEnableAdaptivePointBudget(true);
        } else {
          const pointBudget = Math.max(1, Math.min(30, Number.parseInt(parsed.pointBudget)));
          setEnableAdaptivePointBudget(false);
          setPointBudget(pointBudget * 1_000_000);
        }
      }

      setLastDeeplinkUrlParameters(parameters);

      isComplete.current = true;
      setStatesLoaded(true);
    }
  }, [
    structureId,
    inspectionMetadata,
    getAssemblyId,
    setSelectedAssemblyId,
    setStatesLoaded,
    setSelectedStructureDefectsLegends,
    setSelectedAssemblyName,
    structureLocations,
    setSelectedSpherical,
    setCameraTarget,
    setPointOfInterestId,
    setSelectedTemplateFilters,
    setBlisterToAdd,
    setAbyssViewerState,
    lastDeeplinkUrlParameters,
    setLastDeeplinkUrlParameters,
    setShowOnlySelectedPoi,
    setDrawerLeft,
    drawerLeft,
    setEnableAdaptivePointBudget,
    setPointBudget,
  ]);
};
